import { useState, useEffect } from 'react';
import { X, Gift, Sparkles } from 'lucide-react';
import BookingModal from './BookingModal';

const DiscountPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed the popup in this session
    const hasSeenPopup = sessionStorage.getItem('discountPopupDismissed');
    
    if (!hasSeenPopup) {
      // Show popup after 3 seconds
      const timer = setTimeout(() => {
        setShowPopup(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setShowPopup(false);
    setDismissed(true);
    sessionStorage.setItem('discountPopupDismissed', 'true');
  };

  const handleBookNow = () => {
    setShowPopup(false);
    setDismissed(true);
    sessionStorage.setItem('discountPopupDismissed', 'true');
    setIsBookingModalOpen(true);
  };

  if (dismissed && !isBookingModalOpen) return null;

  return (
    <>
      {/* Popup Overlay */}
      {showPopup && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn"
          onClick={handleDismiss}
        >
          {/* Popup Container */}
          <div 
            className="relative bg-black border-2 border-[#B8860B] rounded-lg max-w-md w-full overflow-hidden animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10"
              aria-label="Close popup"
              data-testid="discount-popup-close"
            >
              <X size={24} />
            </button>

            {/* Decorative Top Banner */}
            <div className="bg-gradient-to-r from-[#B8860B] via-[#D4AF37] to-[#B8860B] py-3 px-6">
              <div className="flex items-center justify-center gap-2">
                <Sparkles size={20} className="text-black" />
                <span className="text-black font-bold text-sm tracking-wider uppercase">
                  Limited Time Offer
                </span>
                <Sparkles size={20} className="text-black" />
              </div>
            </div>

            {/* Content */}
            <div className="p-8 text-center">
              {/* Gift Icon */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#B8860B]/20 flex items-center justify-center">
                <Gift size={40} className="text-[#B8860B]" />
              </div>

              {/* Headline */}
              <h2 className="text-3xl md:text-4xl font-light text-white mb-2">
                Book Now & Get
              </h2>
              <div className="text-5xl md:text-6xl font-bold text-[#B8860B] mb-2">
                10% OFF
              </div>
              <p className="text-white/60 text-lg mb-6">
                on all services booked through our website
              </p>

              {/* Features */}
              <div className="flex flex-wrap justify-center gap-4 mb-8 text-white/50 text-sm">
                <span className="flex items-center gap-1">
                  ✓ All Locations
                </span>
                <span className="flex items-center gap-1">
                  ✓ All Services
                </span>
                <span className="flex items-center gap-1">
                  ✓ No Minimum Order
                </span>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleBookNow}
                className="w-full bg-[#B8860B] text-black py-4 text-lg font-bold tracking-wider uppercase hover:bg-[#9A7B0A] transition-all duration-300 rounded"
                data-testid="popup-book-now"
              >
                Claim Your 10% Off
              </button>

              {/* Subtext */}
              <p className="text-white/40 text-xs mt-4">
                *Valid for online bookings only. Cannot be combined with other offers.
              </p>
            </div>

            {/* Decorative Bottom */}
            <div className="h-1 bg-gradient-to-r from-[#B8860B] via-[#D4AF37] to-[#B8860B]"></div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.9);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default DiscountPopup;
